import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper } from '@mui/material';
import { chain } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import DataTable from 'src/components/common/DataTables/DataTable';
import { ROLES } from 'src/constants';

import { getColumns } from './columnDefinitions';

/**
 * OrdersDataTable - Data table wrapper for orders
 */
function OrdersDataTable({
    orders,
    status,
    profile,
    params,
    rowCount,
    isLoading,
    selectedOrdersIds,
    onRowClick,
    onPageChange,
    onSelectionChange,
    onCreateReturn,
}) {
    const { t } = useTranslation();
    console.log(status);
    
    // Memoized columns
    const columns = useMemo(
        () => getColumns({
            t,
            status,
            profile,
            onCreateReturn,
        }),
        [t, status, profile, onCreateReturn]
    );

    // Checkbox selection logic
    const checkboxSelection = useMemo(() => {
        if (profile?.role === ROLES.admin) {
            return !['ALL_ORDERS', 'ORDER_CANCELED'].includes(status);
        }
        return !['ALL_ORDERS'].includes(status);
    }, [profile?.role, status]);

    // Row className for highlighting based on waiting time
    const getRowClassName = useCallback(({ row }) => {
        try {
            const rowClasses = [];

            if (
                profile?.role === ROLES.admin &&
                row?.otoIntegration?.shipment?.currentStatus === 'pending'
            ) {
                rowClasses.push('row-admin-order-pending');
            }

            if (status === 'ORDER_ASSIGNED_TO_SHIPPING_COMPANY' && profile?.role === ROLES.admin) {
                const date = chain(row?.statusTrack)
                    .filter((el) => el.status === 'ORDER_ASSIGNED_TO_SHIPPING_COMPANY')
                    .last()
                    .get('dateTime')
                    .value();

                if (!date) return rowClasses.join(' ');
                const hours = moment().diff(moment(date), 'hours');
                if (hours >= 72) rowClasses.push('row-waiting-72h');
                else if (hours >= 24) rowClasses.push('row-waiting-24h');
            }

            return rowClasses.join(' ');
        } catch (e) {
            return '';
        }
    }, [status, profile?.role]);

    // Check if row is selectable
    const isRowSelectable = useCallback(
        (params) => params.row?.otoIntegration?.shipment?.currentStatus !== 'pending',
        []
    );

    // Handle row click
    const handleRowClick = useCallback(
        ({ row }) => {
            onRowClick(row);
        },
        [onRowClick]
    );

    return (
        <Box>
            <Paper
                className="order-table"
                sx={{ height: { xs: '70vh', md: '73vh' }, mt: 1 }}
            >
                <DataTable
                    tableId="mapit-ship-orders-table"
                    rows={orders?.docs ?? []}
                    columns={columns}
                    loading={isLoading}
                    rowCount={rowCount}
                    page={params?.page}
                    pageSize={20}
                    onRowClick={handleRowClick}
                    onPageChange={onPageChange}
                    customComponents={['Pagination']}
                    checkboxSelection={checkboxSelection}
                    selectionModel={selectedOrdersIds}
                    getRowClassName={getRowClassName}
                    isRowSelectable={isRowSelectable}
                    onSelectionModelChange={onSelectionChange}
                />
            </Paper>
        </Box>
    );
}

OrdersDataTable.propTypes = {
    orders: PropTypes.object,
    status: PropTypes.string.isRequired,
    profile: PropTypes.object,
    params: PropTypes.object.isRequired,
    rowCount: PropTypes.number,
    isLoading: PropTypes.bool,
    selectedOrdersIds: PropTypes.array.isRequired,
    onRowClick: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onSelectionChange: PropTypes.func.isRequired,
    onCreateReturn: PropTypes.func.isRequired,
};

OrdersDataTable.defaultProps = {
    orders: null,
    profile: null,
    rowCount: 0,
    isLoading: false,
};

export default memo(OrdersDataTable);
